/**
 * Bill Pay Page
 * 4-week upcoming payment schedule with mark-paid, slide-payment,
 * and grace period support. Shows payments grouped by week with totals.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  CreditCard,
  Landmark,
  Repeat,
  Receipt,
  Loader2,
  CheckCircle2,
  Circle,
  ChevronRight,
  CalendarClock,
  DollarSign,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ScheduledPayment {
  sourceId: string;
  source: string;
  name: string;
  lender: string | null;
  paymentType: string;
  dueDate: string;
  scheduledDate: string;
  minimumPayment: number;
  paymentAmount: number;
  currentBalance: number;
  interestRate: number;
  monthlyInterest: number;
  autopayEnabled: boolean;
  graceDays: number;
  paymentStatus: string;
  paidDate: string | null;
  paidAmount: number | null;
  notes: string | null;
  scheduleId: string | null;
  weekIndex: number;
}

interface BusinessProfile {
  id: string;
  name: string;
}

const paymentTypeLabel: Record<string, string> = {
  credit_card: 'Credit Card',
  store_card: 'Store Card',
  business_line: 'Business Line',
  mortgage: 'Mortgage',
  auto: 'Auto Loan',
  equipment: 'Equipment Loan',
  business: 'Business Loan',
  personal: 'Personal Loan',
  line_of_credit: 'Line of Credit',
  subscription: 'Subscription',
  expense: 'Recurring Expense',
  other: 'Other',
};

function getWeekStart(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day;
  const weekStart = new Date(now.getFullYear(), now.getMonth(), diff);
  return weekStart.toISOString().split('T')[0];
}

function formatWeekRange(startDate: string, weekIndex: number): { label: string; start: string; end: string } {
  const start = new Date(startDate);
  start.setDate(start.getDate() + weekIndex * 7);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);

  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const label = weekIndex === 0 ? 'This Week' : weekIndex === 1 ? 'Next Week' : `Week ${weekIndex + 1}`;

  return {
    label,
    start: start.toLocaleDateString('en-US', opts),
    end: end.toLocaleDateString('en-US', opts),
  };
}

function getNextWeekDate(scheduledDate: string): string {
  const d = new Date(scheduledDate);
  d.setDate(d.getDate() + 7);
  return d.toISOString().split('T')[0];
}

export function BillPayPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [profileFilter, setProfileFilter] = useState('');
  const [slideDialogOpen, setSlideDialogOpen] = useState(false);
  const [slidingPayment, setSlidingPayment] = useState<ScheduledPayment | null>(null);
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [payingPayment, setPayingPayment] = useState<ScheduledPayment | null>(null);

  const startDate = getWeekStart();

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['paymentSchedule', { startDate, businessProfileId: profileFilter }],
    queryFn: () => api.getPaymentSchedule({
      startDate,
      weeks: 4,
      businessProfileId: profileFilter || undefined,
    }),
  });

  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const payments = (data?.data || []) as ScheduledPayment[];

  // Group by week
  const weeks: Map<number, ScheduledPayment[]> = new Map();
  for (let i = 0; i < 4; i++) weeks.set(i, []);
  for (const pmt of payments) {
    const wi = Math.max(0, Math.min(3, pmt.weekIndex));
    weeks.get(wi)!.push(pmt);
  }

  const invalidateSchedule = () => {
    queryClient.invalidateQueries({ queryKey: ['paymentSchedule'] });
    queryClient.invalidateQueries({ queryKey: ['upcomingPayments'] });
  };

  const markPaidMutation = useMutation({
    mutationFn: (data: { source: string; sourceId: string; dueDate: string; paidAmount?: number; notes?: string }) =>
      api.markPaymentPaid(data),
    onSuccess: () => {
      invalidateSchedule();
      setPaidDialogOpen(false);
      setPayingPayment(null);
      toast({ title: 'Payment marked as paid' });
    },
  });

  const unmarkMutation = useMutation({
    mutationFn: (data: { source: string; sourceId: string; dueDate: string }) =>
      api.unmarkPayment(data),
    onSuccess: () => {
      invalidateSchedule();
      toast({ title: 'Payment unmarked' });
    },
  });

  const slideMutation = useMutation({
    mutationFn: (data: { source: string; sourceId: string; dueDate: string; newScheduledDate: string; notes?: string }) =>
      api.slidePayment(data),
    onSuccess: () => {
      invalidateSchedule();
      setSlideDialogOpen(false);
      setSlidingPayment(null);
      toast({ title: 'Payment slid to new date' });
    },
  });

  function handleQuickPaid(pmt: ScheduledPayment) {
    setPayingPayment(pmt);
    setPaidDialogOpen(true);
  }

  function handleTogglePaid(pmt: ScheduledPayment) {
    if (pmt.paymentStatus === 'paid') {
      unmarkMutation.mutate({
        source: pmt.source,
        sourceId: pmt.sourceId,
        dueDate: pmt.dueDate.split('T')[0],
      });
    } else {
      handleQuickPaid(pmt);
    }
  }

  function handleSlideNext(pmt: ScheduledPayment) {
    const newDate = getNextWeekDate(pmt.scheduledDate.split('T')[0]);
    slideMutation.mutate({
      source: pmt.source,
      sourceId: pmt.sourceId,
      dueDate: pmt.dueDate.split('T')[0],
      newScheduledDate: newDate,
    });
  }

  function handleSlideCustom(pmt: ScheduledPayment) {
    setSlidingPayment(pmt);
    setSlideDialogOpen(true);
  }

  function handleSlideSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!slidingPayment) return;
    const formData = new FormData(e.currentTarget);
    slideMutation.mutate({
      source: slidingPayment.source,
      sourceId: slidingPayment.sourceId,
      dueDate: slidingPayment.dueDate.split('T')[0],
      newScheduledDate: formData.get('newDate') as string,
      notes: (formData.get('notes') as string) || undefined,
    });
  }

  function handlePaidSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!payingPayment) return;
    const formData = new FormData(e.currentTarget);
    const amountStr = formData.get('paidAmount') as string;
    markPaidMutation.mutate({
      source: payingPayment.source,
      sourceId: payingPayment.sourceId,
      dueDate: payingPayment.dueDate.split('T')[0],
      paidAmount: amountStr ? parseFloat(amountStr) : undefined,
      notes: (formData.get('notes') as string) || undefined,
    });
  }

  // Summary totals
  const totalDue = payments.filter(p => p.paymentStatus !== 'paid').reduce((s, p) => s + p.paymentAmount, 0);
  const totalPaid = payments.filter(p => p.paymentStatus === 'paid').reduce((s, p) => s + (p.paidAmount || p.paymentAmount), 0);
  const totalAll = payments.reduce((s, p) => s + p.paymentAmount, 0);
  const paidCount = payments.filter(p => p.paymentStatus === 'paid').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bill Pay</h1>
          <p className="text-muted-foreground">4-week payment schedule — check off paid bills and slide payments within grace periods</p>
        </div>
        {profiles.length > 1 && (
          <Select value={profileFilter} onValueChange={(v) => setProfileFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All profiles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All profiles</SelectItem>
              {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10"><DollarSign className="h-5 w-5 text-primary" /></div>
              <div>
                <p className="text-sm text-muted-foreground">4-Week Total</p>
                <p className="text-xl font-bold">{formatCurrency(totalAll)}</p>
                <p className="text-xs text-muted-foreground">{payments.length} payment{payments.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10"><CalendarClock className="h-5 w-5 text-amber-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Remaining Due</p>
                <p className="text-xl font-bold">{formatCurrency(totalDue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10"><CheckCircle2 className="h-5 w-5 text-green-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Paid</p>
                <p className="text-xl font-bold">{formatCurrency(totalPaid)}</p>
                <p className="text-xs text-muted-foreground">{paidCount} of {payments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10"><AlertTriangle className="h-5 w-5 text-red-500" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-xl font-bold">
                  {payments.filter(p => p.paymentStatus !== 'paid' && new Date(p.scheduledDate) < new Date(new Date().toISOString().split('T')[0])).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Grid */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : payments.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CalendarClock className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No upcoming payments</p>
            <p className="text-sm text-muted-foreground mt-1">Set a payment due day on your loans or add charge accounts to see them here</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Array.from(weeks.entries()).map(([weekIdx, weekPayments]) => {
            const weekInfo = formatWeekRange(startDate, weekIdx);
            const weekTotal = weekPayments.reduce((s, p) => s + p.paymentAmount, 0);
            const weekPaid = weekPayments.filter(p => p.paymentStatus === 'paid').length;
            const weekPending = weekPayments.length - weekPaid;

            return (
              <div key={weekIdx} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold">{weekInfo.label}</h3>
                    <span className="text-sm text-muted-foreground">{weekInfo.start} &ndash; {weekInfo.end}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    {weekPaid > 0 && (
                      <Badge variant="success" className="text-xs">{weekPaid} paid</Badge>
                    )}
                    {weekPending > 0 && (
                      <Badge variant="warning" className="text-xs">{weekPending} pending</Badge>
                    )}
                    <span className="text-sm font-bold">{formatCurrency(weekTotal)}</span>
                  </div>
                </div>

                {weekPayments.length === 0 ? (
                  <Card>
                    <CardContent className="py-6 text-center text-sm text-muted-foreground">
                      No payments due this week
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {weekPayments.map((pmt) => {
                          const isPaid = pmt.paymentStatus === 'paid';
                          const today = new Date().toISOString().split('T')[0];
                          const isOverdue = !isPaid && pmt.scheduledDate.split('T')[0] < today;
                          const isSlid = pmt.scheduledDate.split('T')[0] !== pmt.dueDate.split('T')[0];
                          const graceEnd = new Date(pmt.dueDate);
                          graceEnd.setDate(graceEnd.getDate() + pmt.graceDays);
                          const withinGrace = !isPaid && pmt.graceDays > 0 && new Date(pmt.scheduledDate) <= graceEnd;

                          return (
                            <div
                              key={`${pmt.source}-${pmt.sourceId}-${pmt.dueDate}`}
                              className={`flex items-center gap-4 p-4 ${isPaid ? 'bg-green-500/5 opacity-75' : isOverdue ? 'bg-red-500/5' : 'hover:bg-muted/30'}`}
                            >
                              {/* Check button */}
                              <button
                                onClick={() => handleTogglePaid(pmt)}
                                className="flex-shrink-0"
                                disabled={markPaidMutation.isPending || unmarkMutation.isPending}
                              >
                                {isPaid ? (
                                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                                ) : (
                                  <Circle className="h-6 w-6 text-muted-foreground hover:text-primary" />
                                )}
                              </button>

                              {/* Icon */}
                              <div className="flex-shrink-0">
                                {pmt.source === 'charge_account' ? <CreditCard className="h-5 w-5 text-muted-foreground" />
                                  : pmt.source === 'subscription' ? <Repeat className="h-5 w-5 text-muted-foreground" />
                                  : pmt.source === 'expense' ? <Receipt className="h-5 w-5 text-muted-foreground" />
                                  : <Landmark className="h-5 w-5 text-muted-foreground" />}
                              </div>

                              {/* Details */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium ${isPaid ? 'line-through text-muted-foreground' : ''}`}>
                                    {pmt.name}
                                  </span>
                                  {pmt.lender && (
                                    <span className="text-xs text-muted-foreground">({pmt.lender})</span>
                                  )}
                                  {pmt.autopayEnabled && (
                                    <Badge variant="secondary" className="text-xs">Autopay</Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                  <span>{paymentTypeLabel[pmt.paymentType] || pmt.paymentType}</span>
                                  <span>&middot;</span>
                                  <span>Due {new Date(pmt.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                  {isSlid && (
                                    <>
                                      <ArrowRight className="h-3 w-3" />
                                      <span className="text-amber-600">
                                        Moved to {new Date(pmt.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      </span>
                                    </>
                                  )}
                                  {pmt.graceDays > 0 && (
                                    <span>&middot; {pmt.graceDays}d grace</span>
                                  )}
                                  {withinGrace && (
                                    <Badge variant="info" className="text-xs">In grace</Badge>
                                  )}
                                  {isOverdue && (
                                    <Badge variant="destructive" className="text-xs">Overdue</Badge>
                                  )}
                                  {isPaid && pmt.paidDate && (
                                    <span className="text-green-600">
                                      Paid {new Date(pmt.paidDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      {pmt.paidAmount != null && ` — ${formatCurrency(pmt.paidAmount)}`}
                                    </span>
                                  )}
                                </div>
                                {pmt.notes && (
                                  <p className="text-xs text-muted-foreground mt-1 italic">{pmt.notes}</p>
                                )}
                              </div>

                              {/* Interest */}
                              {pmt.monthlyInterest > 0 && (
                                <div className="text-right flex-shrink-0">
                                  <span className="text-xs text-amber-600">{formatCurrency(pmt.monthlyInterest)} int.</span>
                                </div>
                              )}

                              {/* Amount */}
                              <div className="text-right flex-shrink-0 w-28">
                                <span className={`text-lg font-bold ${isPaid ? 'text-green-600 line-through' : ''}`}>
                                  {formatCurrency(pmt.paymentAmount)}
                                </span>
                              </div>

                              {/* Actions */}
                              {!isPaid && (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    title="Slide to next week"
                                    onClick={() => handleSlideNext(pmt)}
                                    disabled={slideMutation.isPending}
                                  >
                                    <ChevronRight className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    title="Slide to custom date"
                                    onClick={() => handleSlideCustom(pmt)}
                                  >
                                    <CalendarClock className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Mark Paid Dialog */}
      <Dialog open={paidDialogOpen} onOpenChange={setPaidDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Mark Payment Paid</DialogTitle>
          </DialogHeader>
          {payingPayment && (
            <form onSubmit={handlePaidSubmit} className="space-y-4">
              <div className="text-sm">
                <span className="font-medium">{payingPayment.name}</span>
                <span className="text-muted-foreground ml-2">
                  Due {new Date(payingPayment.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
              <div>
                <Label htmlFor="paidAmount">Amount Paid</Label>
                <Input
                  type="number"
                  name="paidAmount"
                  id="paidAmount"
                  step="0.01"
                  min="0"
                  defaultValue={payingPayment.paymentAmount}
                  placeholder={`Scheduled: ${formatCurrency(payingPayment.paymentAmount)}`}
                />
              </div>
              <div>
                <Label htmlFor="paidNotes">Notes</Label>
                <Textarea name="notes" id="paidNotes" rows={2} placeholder="Optional" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setPaidDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={markPaidMutation.isPending}>
                  {markPaidMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Mark Paid
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Slide Payment Dialog */}
      <Dialog open={slideDialogOpen} onOpenChange={setSlideDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Reschedule Payment</DialogTitle>
          </DialogHeader>
          {slidingPayment && (
            <form onSubmit={handleSlideSubmit} className="space-y-4">
              <div className="text-sm">
                <span className="font-medium">{slidingPayment.name}</span>
                <span className="text-muted-foreground ml-2">
                  Due {new Date(slidingPayment.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                {slidingPayment.graceDays > 0 && (
                  <span className="text-xs text-muted-foreground ml-2">({slidingPayment.graceDays}d grace period)</span>
                )}
              </div>
              <div>
                <Label htmlFor="newDate">New Payment Date</Label>
                <Input
                  type="date"
                  name="newDate"
                  id="newDate"
                  required
                  defaultValue={getNextWeekDate(slidingPayment.scheduledDate.split('T')[0])}
                />
              </div>
              <div>
                <Label htmlFor="slideNotes">Notes</Label>
                <Textarea name="notes" id="slideNotes" rows={2} placeholder="e.g., Within grace period" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setSlideDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={slideMutation.isPending}>
                  {slideMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Reschedule
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
